import { z } from 'zod';
import { createPhoneSchema } from '@/components/FormFieldPhone';
import { ZIP_CODE_SCHEMA } from '@/utils/constant';
import type { HIAttemptEmailLog } from '@/types/homeInspection.types';

const EMOJI_REGEX = /\p{Extended_Pictographic}/u;

// ── Enums ──────────────────────────────────────────────────────────────────

export const YES_NO_OPTIONS = [
  { value: 'YES', label: 'Yes' },
  { value: 'NO', label: 'No' },
];

export const MARITAL_STATUS_OPTIONS = [
  { value: 'MARRIED', label: 'Married' },
  { value: 'SINGLE', label: 'Single' },
];

export const HOUSE_EXTERIOR_OPTIONS = [
  { value: 'VINYL_SIDING', label: 'Vinyl Siding' },
  { value: 'WOOD_SIDING', label: 'Wood Siding' },
  { value: 'SHINGLES', label: 'Shingles' },
  { value: 'BRICK', label: 'Brick' },
  { value: 'OTHER', label: 'Other' },
];

export const HOUSE_TYPE_OPTIONS = [
  { value: 'SINGLE_FAMILY', label: 'Single Family' },
  { value: 'TWO_FAMILY', label: 'Two Family' },
  { value: 'MULTI_FAMILY', label: 'Multi Family' },
  { value: 'TOWNHOUSE', label: 'Townhouse' },
  { value: 'CONDO', label: 'Condo' },
];

export const GARAGE_OPTIONS = [
  { value: 'NONE', label: 'None' },
  { value: 'ONE_CAR', label: 'One Car' },
  { value: 'TWO_CARS', label: 'Two Cars' },
  { value: 'THREE_OR_MORE_CARS', label: 'Three or More Cars' },
];

export const MAX_ATTEMPT_IMAGES = 50;
export const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
export const ALLOWED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heif',
  'image/heic',
  'image/raw',
];

export const ALLOWED_IMAGE_EXTENSIONS = ['jpeg', 'jpg', 'png', 'heif', 'heic', 'raw'];

export const isAllowedImageFile = (file: File): boolean => {
  if (file.type && ALLOWED_IMAGE_MIME_TYPES.includes(file.type)) return true;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return ALLOWED_IMAGE_EXTENSIONS.includes(ext);
};

// ── Step 1: Owner Information ──────────────────────────────────────────────

export const HiAttemptStep1Schema = z
  .object({
    dateOfAttempt: z.date({ required_error: 'Date of Attempt is required' }),
    timeOfAttempt: z
      .string({ required_error: 'Time of Attempt is required' })
      .min(1, 'Time of Attempt is required')
      .regex(/^\d{2}:\d{2} (AM|PM)$/, 'Time must be in hh:mm AM/PM format'),
    ownerAddressLine1: z.string().nullable().optional(),
    ownerAddressLine2: z.string().nullable().optional(),
    ownerCity: z.string().nullable().optional(),
    ownerState: z
      .string()
      .nullable()
      .optional()
      .refine((v) => !v || /^[a-zA-Z]{2}$/.test(v), { message: 'State must be 2 letters' }),
    ownerZipCode: ZIP_CODE_SCHEMA.optional().nullable(),
    ownerPhone: createPhoneSchema(false),
    maritalStatus: z.enum(['MARRIED', 'SINGLE']).nullable().optional(),
    spouseName: z.string().nullable().optional(),
    ownerOccupied: z.enum(['YES', 'NO']).nullable().optional(),
    vacant: z.enum(['YES', 'NO']).nullable().optional(),
    bothOwnersResideTogether: z.enum(['YES', 'NO']).nullable().optional(),
    coOwnerName: z.string().nullable().optional(),
    coOwnerAddressLine1: z.string().nullable().optional(),
    coOwnerAddressLine2: z.string().nullable().optional(),
    coOwnerCity: z.string().nullable().optional(),
    coOwnerState: z
      .string()
      .nullable()
      .optional()
      .refine((v) => !v || /^[a-zA-Z]{2}$/.test(v), { message: 'State must be 2 letters' }),
    coOwnerZipCode: ZIP_CODE_SCHEMA.optional().nullable(),
    defendantInArmedForces: z.enum(['YES', 'NO']).nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.maritalStatus === 'MARRIED' && !data.spouseName?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Spouse Name is required when Marital Status is Married',
        path: ['spouseName'],
      });
    }
  });

// ── Step 2: Tenant Information ─────────────────────────────────────────────

const rentAmountSchema = z
  .preprocess(
    (val) => {
      if (val === null || val === undefined || val === '') return undefined;
      const num = Number(val);
      return Number.isNaN(num) ? val : num;
    },
    z
      .number({ invalid_type_error: 'Rent amount must be a number' })
      .positive({ message: 'Rent amount must be positive' })
      .multipleOf(0.01, { message: 'Rent amount can have at most 2 decimal places' })
      .nullable()
      .optional()
  )
  .nullable()
  .optional();

export const HiAttemptStep2Schema = z.object({
  tenantOccupied: z.enum(['YES', 'NO']).nullable().optional(),
  tenants: z
    .array(
      z.object({
        name: z.string().min(1, 'Tenant name is required'),
        floor: z
          .string()
          .nullable()
          .optional()
          .refine((v) => !v || /^[a-zA-Z0-9]+$/.test(v), { message: 'Floor must be alphanumeric' }),
        relative: z.enum(['YES', 'NO']).nullable().optional(),
        lease: z.enum(['YES', 'NO']).nullable().optional(),
        rentAmount: rentAmountSchema,
      })
    )
    .optional(),
});

// ── Step 3: Property Information ───────────────────────────────────────────

export const HiAttemptStep3Schema = z
  .object({
    houseExterior: z.enum(['VINYL_SIDING', 'WOOD_SIDING', 'SHINGLES', 'BRICK', 'OTHER'], {
      required_error: 'House Exterior is required',
    }),
    houseExteriorOther: z.string().nullable().optional(),
    houseType: z.enum(['SINGLE_FAMILY', 'TWO_FAMILY', 'MULTI_FAMILY', 'TOWNHOUSE', 'CONDO'], {
      required_error: 'House Type is required',
    }),
    numberOfUnits: z.preprocess(
      (val) => {
        if (val === null || val === undefined || val === '') return undefined;
        const num = Number(val);
        return Number.isNaN(num) ? val : num;
      },
      z
        .number({ required_error: 'Number of Units is required', invalid_type_error: 'Number of Units must be a number' })
        .int('Number of Units must be a whole number')
        .min(1, 'Number of Units must be at least 1')
        .max(100, 'Number of Units cannot exceed 100')
    ),
    garage: z.enum(['NONE', 'ONE_CAR', 'TWO_CARS', 'THREE_OR_MORE_CARS'], {
      required_error: 'Garage is required',
    }),
    sale: z.enum(['YES', 'NO'], { required_error: 'Sale is required' }),
    saleBy: z.string().nullable().optional(),
    salePhone: createPhoneSchema(false),
    mediaIds: z
      .array(z.string().uuid())
      .max(MAX_ATTEMPT_IMAGES, `Maximum ${MAX_ATTEMPT_IMAGES} images allowed`)
      .optional()
      .default([]),
  })
  .superRefine((data, ctx) => {
    if (data.houseExterior === 'OTHER' && !data.houseExteriorOther?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Other Exterior description is required when House Exterior is Other',
        path: ['houseExteriorOther'],
      });
    }
    if (data.sale === 'YES' && !data.saleBy?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'By is required when Sale is Yes',
        path: ['saleBy'],
      });
    }
  });

// ── Step 4: Comments ───────────────────────────────────────────────────────

export const HiAttemptStep4Schema = z.object({
  comments: z
    .string()
    .nullable()
    .optional()
    .refine((v) => !v || !EMOJI_REGEX.test(v), { message: 'Emojis are not allowed in comments' }),
  inspectorName: z.string().optional(),
});

// ── Vacancy Checklist (Vacant & Abandoned / first PS+HI attempt) ────────────

export const HiAttemptVacancyChecklistSchema = z
  .object({
    overgrownOrNeglectedVegetation: z.boolean().nullable().optional(),
    hazardousNoxiousOrUnhealthySubstances: z.boolean().nullable().optional(),
    newspapersCircularsFlyersOrMail: z.boolean().nullable().optional(),
    utilitiesDisconnected: z.boolean().nullable().optional(),
    junkLitterTrashOrDebris: z.boolean().nullable().optional(),
    noWindowTreatments: z.boolean().nullable().optional(),
    noFurnishingsOrPersonalItems: z.boolean().nullable().optional(),
    neighborStatements: z.boolean().nullable().optional(),
    neighborStatementsSummary: z.string().nullable().optional(),
    boardedOrBrokenWindows: z.boolean().nullable().optional(),
    brokenOrUnlockedDoors: z.boolean().nullable().optional(),
    riskToHealthSafetyOrWelfare: z.boolean().nullable().optional(),
    riskToHealthSafetyOrWelfareDetails: z.string().nullable().optional(),
    codeViolationOrVacancyOrder: z.boolean().nullable().optional(),
    winterizedByMortgagee: z.boolean().nullable().optional(),
    writtenStatementOfAbandonment: z.boolean().nullable().optional(),
    otherIndiciaOfAbandonment: z.boolean().nullable().optional(),
    otherIndiciaOfAbandonmentDetails: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.neighborStatements && !data.neighborStatementsSummary?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Statements summary is required when the statements checkbox is selected',
        path: ['neighborStatementsSummary'],
      });
    }
    if (data.riskToHealthSafetyOrWelfare && !data.riskToHealthSafetyOrWelfareDetails?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Risk details are required when the risk checkbox is selected',
        path: ['riskToHealthSafetyOrWelfareDetails'],
      });
    }
    if (data.otherIndiciaOfAbandonment && !data.otherIndiciaOfAbandonmentDetails?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Other abandonment details are required when the other indicia checkbox is selected',
        path: ['otherIndiciaOfAbandonmentDetails'],
      });
    }
  });

export type HiAttemptVacancyChecklistValues = z.infer<typeof HiAttemptVacancyChecklistSchema>;

/** Owner step without date/time — used when PS Service Information already collected them. */
export const HiAttemptOwnerFieldsSchema = HiAttemptStep1Schema.innerType()
  .omit({
    dateOfAttempt: true,
    timeOfAttempt: true,
  })
  .superRefine((data, ctx) => {
    if (data.maritalStatus === 'MARRIED' && !data.spouseName?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Spouse Name is required when Marital Status is Married',
        path: ['spouseName'],
      });
    }
  });

export const HiAttemptPropertyAndCommentsSchema = HiAttemptStep3Schema.and(HiAttemptStep4Schema);

// ── Combined types ─────────────────────────────────────────────────────────

export type HiAttemptFormValues = z.infer<typeof HiAttemptStep1Schema> &
  z.infer<typeof HiAttemptStep2Schema> &
  z.infer<typeof HiAttemptStep3Schema> &
  z.infer<typeof HiAttemptStep4Schema> & {
    vacancyChecklist?: HiAttemptVacancyChecklistValues | null;
  };

export const HI_ATTEMPT_STEP_SCHEMAS = [
  HiAttemptStep1Schema,
  HiAttemptStep2Schema,
  HiAttemptStep3Schema,
  HiAttemptStep4Schema,
] as const;

// ── API payload type (matches HomeInspectionAttemptCreateRequest) ──────────

export type HiAttemptTenantPayload = {
  name: string;
  floor?: string | null;
  relative?: 'YES' | 'NO';
  lease?: 'YES' | 'NO';
  rentAmount?: number;
};

export type HiAttemptCreatePayload = {
  dateOfAttempt: string;
  timeOfAttempt: string;
  ownerAddressLine1?: string | null;
  ownerAddressLine2?: string | null;
  ownerCity?: string | null;
  ownerState?: string | null;
  ownerZipCode?: string | null;
  ownerPhone?: string | null;
  maritalStatus?: 'MARRIED' | 'SINGLE' | null;
  spouseName?: string | null;
  ownerOccupied?: 'YES' | 'NO' | null;
  vacant?: 'YES' | 'NO' | null;
  bothOwnersResideTogether?: 'YES' | 'NO' | null;
  coOwnerName?: string | null;
  coOwnerAddressLine1?: string | null;
  coOwnerAddressLine2?: string | null;
  coOwnerCity?: string | null;
  coOwnerState?: string | null;
  coOwnerZipCode?: string | null;
  defendantInArmedForces?: 'YES' | 'NO' | null;
  tenantOccupied?: 'YES' | 'NO' | null;
  tenants?: HiAttemptTenantPayload[] | null;
  relative?: 'YES' | 'NO' | null;
  lease?: 'YES' | 'NO' | null;
  rentAmount?: number | null;
  houseExterior: 'VINYL_SIDING' | 'WOOD_SIDING' | 'SHINGLES' | 'BRICK' | 'OTHER';
  houseExteriorOther?: string;
  houseType: 'SINGLE_FAMILY' | 'TWO_FAMILY' | 'MULTI_FAMILY' | 'TOWNHOUSE' | 'CONDO';
  numberOfUnits: number;
  garage: 'NONE' | 'ONE_CAR' | 'TWO_CARS' | 'THREE_OR_MORE_CARS';
  sale: 'YES' | 'NO';
  saleBy?: string;
  salePhone?: string;
  saleDate?: string;
  comments?: string;
  mediaIds: string[];
  vacancyChecklist?: HiAttemptVacancyChecklistValues | null;
};

// ── API response type (matches HomeInspectionAttemptCreateResponse) ─────────

export interface HiAttemptMedia {
  attemptMediaId: string;
  attemptId: string;
  mediaId: string;
  createdAt: string;
  media: {
    mediaId: string;
    type: string;
    mimeType: string;
    path: string;
    bucketName: string;
    baseUrl: string;
    createdAt: string;
  };
}

export interface HiAttemptUserRef {
  userId: string;
  firstName: string;
  lastName: string;
}

export interface HiAttemptResponse {
  attemptId: string;
  workOrderId: string;
  dateOfAttempt: string;
  timeOfAttempt: string;
  ownerAddressLine1: string | null;
  ownerAddressLine2: string | null;
  ownerCity: string | null;
  ownerState: string | null;
  ownerZipCode: string | null;
  ownerPhone: string | null;
  maritalStatus: 'MARRIED' | 'SINGLE' | null;
  spouseName: string | null;
  ownerOccupied: 'YES' | 'NO' | null;
  vacant: 'YES' | 'NO' | null;
  bothOwnersResideTogether: 'YES' | 'NO' | null;
  coOwnerName: string | null;
  coOwnerAddressLine1: string | null;
  coOwnerAddressLine2: string | null;
  coOwnerCity: string | null;
  coOwnerState: string | null;
  coOwnerZipCode: string | null;
  defendantInArmedForces: 'YES' | 'NO' | null;
  tenantOccupied: 'YES' | 'NO' | null;
  relative: 'YES' | 'NO' | null;
  lease: 'YES' | 'NO' | null;
  rentAmount: number | null;
  houseExterior: string;
  houseExteriorOther: string | null;
  houseType: string;
  numberOfUnits: number;
  garage: string;
  sale: string;
  saleBy: string | null;
  salePhone: string | null;
  saleDate: string | null;
  comments: string | null;
  inspectorId: string | null;
  inspector: HiAttemptUserRef | null;
  createdById: string;
  createdBy: HiAttemptUserRef;
  updatedById: string | null;
  updatedBy: HiAttemptUserRef | null;
  editableUntil: string | null;
  createdAt: string;
  updatedAt: string;
  tenants: {
    tenantId: string;
    attemptId: string;
    name: string;
    floor: string | null;
    relative?: 'YES' | 'NO' | null;
    lease?: 'YES' | 'NO' | null;
    rentAmount?: number | string | null;
  }[];
  media: HiAttemptMedia[];
}

export interface HiAttemptCreateResponse {
  workOrder: import('@/types/homeInspection.types').HomeInspectionWorkOrderResponse;
  attempt: HiAttemptResponse;
}

export type HiAttemptUpdatePayload = HiAttemptCreatePayload;
export type HiAttemptUpdateResponse = HiAttemptCreateResponse;
export type HiAttemptGetResponse = HiAttemptResponse;

export interface HiAttemptListResponse {
  attempts: HiAttemptGetResponse[];
  /** Attempt share-update email logs (last 5, newest first). */
  emailLogs: HIAttemptEmailLog[];
}
